import { useMemo } from 'react';
import { Box, Flex, Text, chakra } from '@chakra-ui/react';
import { Check } from 'lucide-react';
import {
  formatShiftAvailability,
  formatShiftTime,
  groupShiftsByDay,
  isShiftFull,
  type EventShift,
} from '@/utils/eventShifts';

interface ShiftListProps {
  shifts: EventShift[];
  /** When given together with onToggle, every row becomes a checkbox. */
  selectedIds?: string[];
  onToggle?: (shiftId: string) => void;
  /** Full shifts cannot be newly selected. One that is already selected can still be cleared. */
  blockFull?: boolean;
  /** Tag the shifts the signed-in user is on. */
  highlightMine?: boolean;
  /** A short line under a shift's time, keyed by shift id (e.g. an overlap warning). */
  notes?: Record<string, string>;
  disabled?: boolean;
}

/** An event's shifts grouped by day, read-only or selectable. */
export function ShiftList({
  shifts,
  selectedIds,
  onToggle,
  blockFull = false,
  highlightMine = false,
  notes,
  disabled = false,
}: ShiftListProps) {
  const days = useMemo(() => groupShiftsByDay(shifts), [shifts]);
  const selectable = Boolean(selectedIds && onToggle);

  return (
    <Box display="flex" flexDirection="column" gap="3">
      {days.map((day) => (
        <Box key={day.dayKey}>
          <Text fontSize="xs" fontWeight="semibold" color="volyx.textSecondary" textTransform="uppercase" mb="1.5">
            {day.label}
          </Text>
          <Box display="flex" flexDirection="column" gap="1.5">
            {day.shifts.map((shift) => {
              const selected = selectedIds?.includes(shift.id) ?? false;
              const full = isShiftFull(shift);
              const locked = disabled || (blockFull && full && !selected);
              const content = (
                <>
                  {selectable && (
                    <Flex
                      w="18px"
                      h="18px"
                      flexShrink={0}
                      borderRadius="md"
                      border="2px solid"
                      borderColor={selected ? 'volyx.action' : 'volyx.mediumGray'}
                      bg={selected ? 'volyx.action' : 'transparent'}
                      align="center"
                      justify="center"
                    >
                      {selected && <Check size={12} color="white" strokeWidth={3} />}
                    </Flex>
                  )}
                  <Flex flex="1" minW="0" align="center" gap="2" flexWrap="wrap">
                    <Text as="span" fontSize="sm" fontWeight="medium" color="volyx.text">
                      {formatShiftTime(shift)}
                    </Text>
                    {highlightMine && shift.mine && (
                      <Text
                        as="span"
                        fontSize="10px"
                        fontWeight="bold"
                        px="1.5"
                        py="0.5"
                        borderRadius="md"
                        bg="volyx.primaryLight"
                        color="volyx.action"
                        textTransform="uppercase"
                      >
                        You
                      </Text>
                    )}
                    {notes?.[shift.id] && (
                      <Text as="span" w="full" fontSize="xs" color="volyx.warning">
                        {notes[shift.id]}
                      </Text>
                    )}
                  </Flex>
                  <Text
                    as="span"
                    flexShrink={0}
                    fontSize="xs"
                    fontWeight={full ? 'semibold' : 'normal'}
                    color={full ? 'volyx.error' : 'volyx.textSecondary'}
                  >
                    {formatShiftAvailability(shift)}
                  </Text>
                </>
              );

              const rowStyles = {
                display: 'flex',
                alignItems: 'center',
                gap: '3',
                w: 'full',
                px: '3',
                py: '2.5',
                bg: 'volyx.lightGray',
                borderRadius: 'lg',
                textAlign: 'left',
              } as const;

              if (!selectable) {
                return (
                  <Box key={shift.id} {...rowStyles}>
                    {content}
                  </Box>
                );
              }

              return (
                <chakra.button
                  key={shift.id}
                  type="button"
                  role="checkbox"
                  aria-checked={selected}
                  {...rowStyles}
                  border="2px solid"
                  borderColor={selected ? 'volyx.action' : 'transparent'}
                  cursor={locked ? 'not-allowed' : 'pointer'}
                  opacity={locked && !disabled ? 0.55 : 1}
                  disabled={locked}
                  onClick={() => onToggle?.(shift.id)}
                >
                  {content}
                </chakra.button>
              );
            })}
          </Box>
        </Box>
      ))}
    </Box>
  );
}
